// src/components/Login.js
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { enviarCodigo2FA, verificarCodigo2FA } from "../services/authService";
import { obtenerFuncionesPorEmail } from "../services/permisosService";
import { useAuth } from "../context/AuthContext";
import { computeFirstAllowedRoute } from "../context/firstAllowedRoute";
import { FEATURE_BITS as FB, BITSET_LEN } from "../constants/permisos";
import "./Login.css";
import GoogleSignIn from "./GoogleSignIn";

// ---- helpers permisos ----
const hasBitByKey = (bitset = "", featureKey) => {
  const pos = FB[featureKey];
  if (typeof pos !== "number") return false;
  const i = pos - 1;
  return i >= 0 && i < bitset.length && bitset[i] === "1";
};

// Normaliza a 90 bits y fuerza el bit #1 (INICIO_VIEW) encendido
const normalizeBitset = (s) => {
  const raw = (s || "").toString().padEnd(BITSET_LEN, "0").slice(0, BITSET_LEN);
  return "1" + raw.slice(1);
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [codigo2FA, setCodigo2FA] = useState("");
  const [mostrarCodigo, setMostrarCodigo] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [verificando, setVerificando] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  // Paso 1: validar credenciales y enviar código 2FA
  const handleEnviarCodigo = async (e) => {
    e.preventDefault();
    setMensaje("");
    setEnviando(true);
    try {
      await enviarCodigo2FA(email, password);
      setMostrarCodigo(true);
      setMensaje("✅ Código enviado al correo");
    } catch (error) {
      setMensaje(`❌ Usuario no registrado: ${error.message}`);
    } finally {
      setEnviando(false);
    }
  };

  const reenviarCodigo = async () => {
    setMensaje("");
    setEnviando(true);
    try {
      await enviarCodigo2FA(email, password);
      setMensaje("✅ Código reenviado correctamente");
    } catch (error) {
      setMensaje(`❌ Error al reenviar: ${error.message}`);
    } finally {
      setEnviando(false);
    }
  };

  // Paso 2: verificar código, obtener bitset y navegar
  const handleVerificarCodigo = async (e) => {
    e.preventDefault();
    setVerificando(true);
    setMensaje("");
    try {
      // (a) Verifica 2FA. Puede traer endpoint/bitset directo.
      const {
        token,
        email: emailSrv,
        endpoint: endpointFromLogin,
        bitset: bitsetFromLogin,
      } = await verificarCodigo2FA(email, codigo2FA);

      // (b) Guarda sesión mínima
      login({ email: emailSrv || email, rol: "permanente", token });

      // (c) Determinar bitset/endpoint de forma robusta
      let endpoint = endpointFromLogin || bitsetFromLogin || "";
      if (!endpoint) {
        try {
          const perms = await obtenerFuncionesPorEmail(emailSrv || email, token);
          endpoint = perms?.bitset || "";
          if (typeof perms?.esSuperAdmin === "boolean") {
            localStorage.setItem("esSuperAdmin", JSON.stringify(perms.esSuperAdmin));
          }
        } catch (fx) {
          console.warn("No se pudieron obtener permisos, continuando con mínimo:", fx);
        }
      }

      // (d) Normalizar y persistir endpoint
      const finalBitset = normalizeBitset(endpoint);
      localStorage.setItem("endpoint", finalBitset);
      window.dispatchEvent(new Event("auth-changed")); // <-- importante para refrescar permisos en contexto

      // (e) Calcular ruta inicial (fallback seguro)
      let firstRoute =
        computeFirstAllowedRoute(finalBitset, hasBitByKey) || "/dashboard/admin";
      if (!firstRoute) {
        if (hasBitByKey(finalBitset, "USUARIOS_TEMP_VIEW"))
          firstRoute = "/usuarios-temporales";
        else if (hasBitByKey(finalBitset, "INICIO_VIEW"))
          firstRoute = "/dashboard/admin";
        else firstRoute = "/dashboard/admin";
      }

      setMensaje("✅ Verificación exitosa");

      // Redirigir…
      navigate(firstRoute, { replace: true });

      // …y forzar recarga (F5) justo después para asegurar la entrada al dashboard
      setTimeout(() => window.location.reload(), 0);
    } catch (error) {
      setMensaje(`❌ Error al verificar el código: ${error.message}`);
    } finally {
      setVerificando(false);
    }
  };

  return (
    <div className="auth-form-wrap">
      <div className="login-card">
        <p className="auth-card-kicker">Acceso seguro</p>
        <h2 className="auth-card-title">
          Ingresa al Sistema
        </h2>
        <p className="auth-card-subtitle">Utiliza tus credenciales y confirma el código enviado a tu correo.</p>

        <form
          onSubmit={mostrarCodigo ? handleVerificarCodigo : handleEnviarCodigo}
          className="auth-form"
        >
          <input
            name="email"
            type="email"
            placeholder="Correo Electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="auth-input"
            required
          />
          <input
            name="password"
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="auth-input"
            required
          />

          {mostrarCodigo && (
            <>
              <input
                name="codigo"
                type="text"
                placeholder="Código de autenticación"
                value={codigo2FA}
                onChange={(e) => setCodigo2FA(e.target.value)}
                className="auth-input auth-reveal"
                required
              />

              <button type="submit" className="auth-primary-button" disabled={verificando}>
                {verificando ? "Verificando..." : "Verificar"}
              </button>

              <button
                type="button"
                onClick={reenviarCodigo}
                className="auth-secondary-button"
                disabled={enviando}
              >
                {enviando ? "Reenviando..." : "Reenviar Código"}
              </button>
            </>
          )}

          {!mostrarCodigo && (
            <button type="submit" className="auth-primary-button" disabled={enviando}>
              {enviando ? "Enviando..." : "Enviar Código"}
            </button>
          )}
        </form>

        {mensaje && <p className={`auth-notice ${mensaje.includes('✅') ? 'auth-notice--success' : 'auth-notice--error'}`} role="status" aria-live="polite">{mensaje}</p>}

        <button
          onClick={() => navigate("/recuperar", { state: { email } })}
          className="auth-link-button"
        >
          Recuperar Contraseña
        </button>

        <div className="auth-divider"><span /><em>o continúa con</em><span /></div>
        <GoogleSignIn />

        <div className="auth-security-label">
          <img src="/jwt-icon.png" alt="jwt" className="h-4" />
          <span>JWT Security</span>
        </div>
      </div>
    </div>
  );
}
